import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { AlertFloating } from '@/components/application/alerts/alerts';
import { Button } from '@/components/base/buttons/button';
import { FormInput } from '@/components/base/form/form-input';
import { notify } from '@/components/base/notifications/notifications';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import {
  createOrganization,
  type CreateOrganizationResult,
} from '@/routes/researchers/onboarding/-utils/create-organization';
import {
  isValidOrganizationWebsite,
  normalizeOrganizationWebsite,
} from '@/routes/researchers/onboarding/-utils/organization-website';

/**
 * Map a {@link CreateOrganizationResult} failure to a user-facing message.
 * The `forbidden-*` and `unauthenticated` cases are race conditions — the
 * user shouldn't be here in those states because `onboarding.tsx`
 * already gated them out — so the copy is intentionally generic.
 */
function failureMessage(
  result: CreateOrganizationResult & { success: false },
  fallback: string,
): string {
  if (result.message) return result.message;
  switch (result.reason) {
    case 'unauthenticated':
      return 'Your session has expired. Please sign in again.';
    case 'forbidden-role':
      return 'Only researcher accounts can create an organization.';
    case 'conflict':
      return 'An organization with that name already exists.';
    case 'bad-request':
      return 'Please double-check the organization details and try again.';
    default:
      // `internal` falls through here; we don't have a more specific
      // message because the underlying core-api error wasn't recognised.
      return fallback;
  }
}

export const Route = createFileRoute('/researchers/onboarding/organization')({
  beforeLoad: ({ context }) => {
    if (context.orgs.length > 0)
      throw redirect({ to: '/researchers/onboarding' });
  },
  component: OnboardingOrganizationPage,
});

function OnboardingOrganizationPage() {
  const { t } = useLingui();
  const navigate = useNavigate();

  const schema = z.object({
    name: z
      .string()
      .trim()
      .min(1, t`Organization name is required`),
    website: z
      .string()
      .trim()
      .min(1, t`Organization website is required`)
      .refine(isValidOrganizationWebsite, t`Please enter a valid website URL`),
  });
  type FormFields = z.infer<typeof schema>;

  const detailsMutation = useMutation<
    CreateOrganizationResult & { success: true },
    Error,
    FormFields
  >({
    mutationFn: async ({ name, website }) => {
      const result = await createOrganization({
        data: {
          name: name.trim(),
          website: normalizeOrganizationWebsite(website),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
      if (!result.success) {
        throw new Error(
          failureMessage(result, t`Failed to create organization`),
        );
      }
      return result;
    },
  });

  const form = useForm({
    defaultValues: { name: '', website: '' },
    validators: { onSubmit: schema },
    onSubmit: ({ value }) =>
      detailsMutation.mutate(value, {
        onSuccess: () => {
          notify({ title: t`Organization details saved`, color: 'success' });
          // The parent onboarding route holds the session-derived org list in
          // its router context. A client navigation would reuse that stale
          // empty list and redirect straight back here. Reload so the next
          // request resolves the organization we just created.
          navigate({
            to: '/researchers/onboarding/personal',
            reloadDocument: true,
          });
        },
      }),
  });

  return (
    <AuthLayout rightPanel={<OnboardingPanel />}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-8"
      >
        <div>
          <p className="text-sm font-semibold tracking-[0.16em] text-brand-secondary uppercase">
            <Trans>Step 1 of 2</Trans>
          </p>
          <h1 className="mt-3 font-heading text-display-xs font-semibold text-primary">
            <Trans>Create your organization</Trans>
          </h1>
          <p className="mt-2 text-md text-secondary">
            <Trans>
              An organization is a shared workspace for you and your team to
              create and run research projects.
            </Trans>
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <FormInput
            type="text"
            form={form}
            field="name"
            label={t`Organization name`}
            placeholder={t`Acme Research`}
            isRequired
            size="md"
          />
          <FormInput
            type="text"
            form={form}
            field="website"
            label={t`Organization website`}
            placeholder={t`https://acme.com`}
            hint={t`Helps participants recognise who's running the research.`}
            onBlur={(website) =>
              form.setFieldValue(
                'website',
                normalizeOrganizationWebsite(website),
              )
            }
            isRequired
            size="md"
          />
        </div>

        {detailsMutation.error && (
          <AlertFloating
            color="error"
            title={t`Something went wrong`}
            description={detailsMutation.error.message}
            confirmLabel=""
          />
        )}

        <Button
          type="submit"
          color="primary"
          size="lg"
          isLoading={detailsMutation.isPending}
          isDisabled={detailsMutation.isPending}
          className="w-full"
          data-testid="researcher-onboarding-next-button"
        >
          <Trans>Next</Trans>
        </Button>
      </form>
    </AuthLayout>
  );
}

function OnboardingPanel() {
  return (
    <div className="relative flex h-full min-h-180 flex-col justify-between overflow-hidden bg-brand-section px-10 py-12 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.22),transparent_34%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.14),transparent_30%)]" />
      <div className="relative">
        <p className="text-sm font-semibold tracking-[0.16em] text-white/70 uppercase">
          <Trans>You&apos;re almost set</Trans>
        </p>
        <h2 className="mt-4 max-w-md font-heading text-display-md font-semibold text-white">
          <Trans>Two quick steps to your first study.</Trans>
        </h2>
        <p className="mt-4 max-w-md text-lg text-white/80">
          <Trans>
            Tell us about your organization and we&apos;ll set up your workspace
            so you can start recruiting right away.
          </Trans>
        </p>
      </div>
    </div>
  );
}
